import type { ReactNode } from 'react';
import { apiErrorMessage } from '../../../../../utils/apiError';
import { type OrthopulseOrderPreview } from '../../../../../services/orthopulseOrder';
import { OrthopulseOrderNotice } from '../../../../shared/OrthopulseBlockedBanner';
import { AddressPill } from '../../../../shared/AddressPill';
import { DmBtnDestructive, DmBtnPrimary, DmBtnSecondary } from '../../dm/DmFormPrimitives';
import { ErrorBanner } from '../../dm/DmBanners';
import type { useSendOrthopulseOrder } from '../../../../../hooks/useOrthopulseOrder';

interface MetaFieldProps {
    label: string;
    value?: string;
    mono?: boolean;
    hint?: string;
    children?: ReactNode;
}

function MetaField({ label, value, mono, hint, children }: MetaFieldProps) {
    return (
        <div className="min-w-0">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                {label}
            </div>
            {children ?? (
                <div
                    className={`text-sm text-gray-800 font-medium truncate ${mono ? 'font-mono' : ''}`}
                >
                    {value}
                </div>
            )}
            {hint && <div className="text-xs text-gray-400 mt-0.5">{hint}</div>}
        </div>
    );
}

interface OrderMetaCardProps {
    vendorLabel: string;
    poNumberPreview: string;
    from: string;
    recipients: string[];
}

// XXXXX is the backend's placeholder for a not-yet-assigned PO on a fresh
// preview - shown alongside a real number only when retrying a failed send.
function OrderMetaCard({ vendorLabel, poNumberPreview, from, recipients }: OrderMetaCardProps) {
    const isPoPlaceholder = poNumberPreview.includes('XXXXX');
    return (
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <MetaField label="Vendor" value={vendorLabel} />
            <MetaField
                label="PO Number"
                value={poNumberPreview}
                mono
                hint={isPoPlaceholder ? 'Assigned when the order is sent' : undefined}
            />
            <MetaField label="From">
                <AddressPill address={from} />
            </MetaField>
            <MetaField label="To">
                <div className="flex flex-wrap gap-1.5">
                    {recipients.map((recipient) => (
                        <AddressPill key={recipient} address={recipient} />
                    ))}
                </div>
            </MetaField>
        </div>
    );
}

type SendOrderMutation = ReturnType<typeof useSendOrthopulseOrder>;

interface OrderPreviewFooterProps {
    canSend: boolean;
    isForcedSend: boolean;
    practiceCharged: boolean;
    isPending: boolean;
    onBack: () => void;
    onSend: () => void;
}

function OrderPreviewFooter({
    canSend,
    isForcedSend,
    practiceCharged,
    isPending,
    onBack,
    onSend
}: OrderPreviewFooterProps) {
    const chargeNote =
        practiceCharged === false
            ? 'Paid by the patient. The practice will not be charged.'
            : 'Sending this order will automatically create the OrthoPulse charge in Billing.';
    return (
        <div className="flex items-center justify-between gap-4">
            <DmBtnSecondary onClick={onBack} disabled={isPending}>
                Back
            </DmBtnSecondary>
            {canSend && (
                <div className="flex items-center gap-4">
                    <p
                        className={`text-xs ${isForcedSend ? 'text-red-700 font-medium' : 'text-gray-500'}`}
                    >
                        {isForcedSend
                            ? 'Overriding the block above - this send is fully audited.'
                            : chargeNote}
                    </p>
                    {isForcedSend ? (
                        <DmBtnDestructive onClick={onSend} disabled={isPending}>
                            {isPending ? 'Sending…' : 'Send anyway'}
                        </DmBtnDestructive>
                    ) : (
                        <DmBtnPrimary onClick={onSend} disabled={isPending}>
                            {isPending ? 'Sending…' : 'Send order'}
                        </DmBtnPrimary>
                    )}
                </div>
            )}
        </div>
    );
}

interface OrderPreviewProps {
    preview: OrthopulseOrderPreview;
    sendOrder: SendOrderMutation;
    onSend: () => void;
    onBack: () => void;
    /** True once an operator with the override permission has opened it and
     *  entered a valid reason - unhides Send despite `preview.blocked`. */
    isForcing?: boolean;
}

// Step 3 of the flow: renders the composed email, then sends for real.
// Back returns to step 1 without discarding the ship-to edits (parent-owned).
function OrderPreview({
    preview,
    sendOrder,
    onSend,
    onBack,
    isForcing = false
}: OrderPreviewProps) {
    // A 'warning' block (charge_without_po) never hides Send or forces - only
    // an 'error' block does, and only once isForcing clears it.
    const isErrorBlock = preview.blocked?.level === 'error';
    const isForcedSend = isErrorBlock && isForcing;
    const canSend = !isErrorBlock || isForcedSend;

    return (
        <div className="flex flex-col gap-4">
            <OrthopulseOrderNotice
                ordersVia={preview.ordersVia}
                blocked={preview.blocked}
                practiceCharged={preview.practiceCharged}
            />

            <OrderMetaCard
                vendorLabel={preview.vendorName}
                poNumberPreview={preview.poNumberPreview}
                from={preview.from}
                recipients={preview.recipients}
            />

            <div className="border border-gray-200 rounded-lg overflow-hidden h-[65vh] bg-gray-50">
                <iframe
                    title="OrthoPulse order email preview"
                    srcDoc={preview.html}
                    sandbox=""
                    className="w-full h-full bg-white"
                />
            </div>

            {sendOrder.isError && (
                <ErrorBanner
                    message={apiErrorMessage(sendOrder.error, 'Failed to send OrthoPulse order')}
                />
            )}

            <OrderPreviewFooter
                canSend={canSend}
                isForcedSend={isForcedSend}
                practiceCharged={preview.practiceCharged}
                isPending={sendOrder.isPending}
                onBack={onBack}
                onSend={onSend}
            />
        </div>
    );
}

export { OrderPreview };
